/**
 * Realtime WebSocket client.
 *
 * Browsers cannot set an Authorization header on a WebSocket handshake, so the
 * access token goes in the query string and the backend verifies it before
 * accepting. Reconnects with backoff, because a dashboard left open overnight
 * will outlive at least one deploy.
 */

import { tokens } from './api';

const WS_URL = import.meta.env.VITE_WS_URL ?? `${location.origin.replace(/^http/, 'ws')}/ws`;

export interface RealtimeEvent {
  type:
    | 'connected'
    | 'ping'
    | 'call.state'
    | 'call.started'
    | 'call.ended'
    | 'transcript'
    | 'qualification'
    | 'transfer'
    | 'usage'
    | 'agent.status';
  tenant_id?: string;
  call_id?: string;
  state?: string;
  from?: string;
  bot_id?: string | null;
  role?: string;
  text?: string;
  seq?: number;
  qualified?: boolean;
  score?: number;
  services?: string[];
  result?: string;
  destination?: string;
  agent?: string | null;
  disposition?: string | null;
  total_client?: number;
  user?: string;
  at?: string;
}

type Handler = (event: RealtimeEvent) => void;

const MAX_BACKOFF_MS = 30_000;

export class RealtimeClient {
  private socket: WebSocket | null = null;
  private handlers = new Set<Handler>();
  private backoff = 1000;
  private closed = false;
  private reconnectTimer: number | null = null;

  constructor(private tenantId?: string) {}

  connect(): void {
    const token = tokens.access();
    if (!token || this.closed) return;

    const url = new URL(WS_URL, window.location.origin);
    url.searchParams.set('token', token);
    if (this.tenantId) url.searchParams.set('tenant_id', this.tenantId);

    const socket = new WebSocket(url.toString());
    this.socket = socket;

    socket.onopen = () => {
      this.backoff = 1000;
    };

    socket.onmessage = (message) => {
      let event: RealtimeEvent;
      try {
        event = JSON.parse(message.data) as RealtimeEvent;
      } catch {
        return;
      }
      // Server heartbeat; nothing to deliver.
      if (event.type === 'ping') return;
      this.handlers.forEach((handler) => handler(event));
    };

    socket.onclose = () => {
      this.socket = null;
      if (this.closed) return;
      this.reconnectTimer = window.setTimeout(() => {
        this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
        this.connect();
      }, this.backoff);
    };

    socket.onerror = () => socket.close();
  }

  subscribe(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
    this.handlers.clear();
  }

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}
